import { useState } from 'react';
import './WorkoutBuilderDemo.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkoutDay {
  day: string;
  name: string;
  modality: string;
  notes: string;
  strength_focus: string;
  equipment: string;
  time: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration_minutes: number;
  other_notes: string;
}

interface SurveyAnswers {
  workout_types: string[];
  best_days: string[];
  preferred_time: string;
  sessions_per_week: string;
  duration: string;
  intensity: string;
  other_notes: string;
}

type Mode = 'build' | 'survey';

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// 30-min slots 5 AM – 10 PM — avoids native time-picker mobile issues
const TIME_OPTIONS: { label: string; value: string }[] = (() => {
  const opts: { label: string; value: string }[] = [{ label: 'Any time', value: '' }];
  for (let h = 5; h <= 22; h++) {
    for (const m of [0, 30]) {
      if (h === 22 && m === 30) continue;
      const hh = String(h).padStart(2, '0');
      const mm = m === 0 ? '00' : '30';
      const ampm = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      opts.push({ label: `${h12}:${mm} ${ampm}`, value: `${hh}:${mm}` });
    }
  }
  return opts;
})();

const MODALITY_OPTIONS = [
  { label: 'Running',      value: 'running',     defaultName: 'Run',               hasNotes: false },
  { label: 'Strength',     value: 'strength',    defaultName: 'Strength Training',  hasNotes: false },
  { label: 'HIIT',         value: 'hiit',        defaultName: 'HIIT Workout',       hasNotes: false },
  { label: 'Yoga',         value: 'yoga',        defaultName: 'Yoga Session',       hasNotes: false },
  { label: 'Mobility',     value: 'mobility',    defaultName: 'Mobility Work',      hasNotes: false },
  { label: 'Core',         value: 'core',        defaultName: 'Core Session',       hasNotes: false },
  { label: 'Stretch',      value: 'stretch',     defaultName: 'Stretch',            hasNotes: false },
  { label: 'Group Class',  value: 'group_class', defaultName: 'Group Class',        hasNotes: true  },
  { label: 'Sport',        value: 'sport',       defaultName: 'Sport',              hasNotes: true  },
  { label: 'Other Cardio', value: 'cardio',      defaultName: 'Cardio Session',     hasNotes: true  },
  { label: 'Other',        value: 'other',       defaultName: 'Workout',            hasNotes: true  },
];

const DEFAULT_NAMES      = Object.fromEntries(MODALITY_OPTIONS.map(o => [o.value, o.defaultName]));
const ALL_DEFAULT_NAMES  = new Set(MODALITY_OPTIONS.map(o => o.defaultName));
const NOTES_MODALITIES   = new Set(MODALITY_OPTIONS.filter(o => o.hasNotes).map(o => o.value));

const STRENGTH_FOCUS = [
  { label: 'Upper Body', value: 'upper_body' },
  { label: 'Lower Body', value: 'lower_body' },
  { label: 'Core',       value: 'core'       },
  { label: 'Full Body',  value: 'full_body'  },
];

const EQUIPMENT = [
  { label: 'Bodyweight',   value: 'bodyweight'   },
  { label: 'Free Weights', value: 'free_weights' },
  { label: 'Full Gym',     value: 'full_gym'     },
];

const DIFFICULTY_OPTIONS: { label: string; value: WorkoutDay['difficulty'] }[] = [
  { label: 'Beginner',     value: 'beginner'     },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced',     value: 'advanced'     },
];

const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90];

const NOTES_LABEL: Record<string, string> = {
  group_class: 'Class name', sport: 'Sport', cardio: 'Activity', other: 'Activity name',
};
const NOTES_PLACEHOLDER: Record<string, string> = {
  group_class: 'e.g. Pilates, Spin, Barre...',
  sport:       'e.g. Tennis, Basketball, Soccer...',
  cardio:      'e.g. Cycling, Rowing, Elliptical...',
  other:       'e.g. Rock climbing, Dance, Martial arts...',
};

// Survey options
const SURVEY_WORKOUT_TYPES = [
  { label: 'Running',       value: 'running'     },
  { label: 'Strength',      value: 'strength'    },
  { label: 'HIIT',          value: 'hiit'        },
  { label: 'Yoga',          value: 'yoga'        },
  { label: 'Mobility',      value: 'mobility'    },
  { label: 'Core & Stretch',value: 'core'        },
  { label: 'Group Classes', value: 'group_class' },
  { label: 'Sports',        value: 'sport'       },
  { label: 'Cycling/Cardio',value: 'cardio'      },
];

const SURVEY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SURVEY_TIMES = [
  { label: 'Early morning', value: 'early_morning', desc: '5–8 AM'   },
  { label: 'Morning',       value: 'morning',       desc: '8–10 AM'  },
  { label: 'Midday',        value: 'midday',        desc: '10 AM–1 PM'},
  { label: 'Afternoon',     value: 'afternoon',     desc: '1–5 PM'   },
  { label: 'Evening',       value: 'evening',       desc: '5–9 PM'   },
  { label: 'Flexible',      value: 'flexible',      desc: 'Any time' },
];

const SURVEY_SPW = [
  { label: '2–3 days', value: '2-3' },
  { label: '3–4 days', value: '3-4' },
  { label: '4–5 days', value: '4-5' },
  { label: '5+ days',  value: '5+'  },
];

const SURVEY_DURATION = [
  { label: '< 30 min',  value: 'under_30'  },
  { label: '30–45 min', value: '30_45'     },
  { label: '45–60 min', value: '45_60'     },
  { label: '60+ min',   value: 'over_60'   },
];

const SURVEY_INTENSITY = [
  { label: 'Light',          value: 'light',       desc: 'Active recovery, low sweat' },
  { label: 'Moderate',       value: 'moderate',    desc: 'Challenging but sustainable' },
  { label: 'Challenging',    value: 'challenging', desc: 'Push yourself most sessions' },
  { label: 'Max effort',     value: 'max',         desc: 'Go hard every time'         },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const blankSession = (): WorkoutDay => ({
  day: '', name: '', modality: '', notes: '', strength_focus: '',
  equipment: '', time: '', difficulty: 'beginner', duration_minutes: 45, other_notes: '',
});

const blankSurvey = (): SurveyAnswers => ({
  workout_types: [], best_days: [], preferred_time: '',
  sessions_per_week: '', duration: '', intensity: '', other_notes: '',
});

function buildAiPrompt(s: SurveyAnswers): string {
  const types = s.workout_types.length
    ? s.workout_types.join(', ')
    : 'any workout type';
  const days = s.best_days.length ? s.best_days.join(', ') : 'flexible days';
  const time = SURVEY_TIMES.find(t => t.value === s.preferred_time)?.label ?? 'flexible time';
  const spw  = s.sessions_per_week || 'flexible frequency';
  const dur  = SURVEY_DURATION.find(d => d.value === s.duration)?.label ?? 'flexible duration';
  const intensity = SURVEY_INTENSITY.find(i => i.value === s.intensity)?.label ?? 'moderate';

  return `You are a personal fitness coach. Create a weekly workout schedule for this user.

Preferences:
- Workout types they enjoy: ${types}
- Best days to work out: ${days}
- Preferred time of day: ${time}
- Sessions per week: ${spw}
- Session length: ${dur}
- Intensity: ${intensity}${s.other_notes ? `\n- Additional notes: ${s.other_notes}` : ''}

Return a JSON array of sessions for one repeating week:
[{
  "day": "Monday",
  "name": "Morning Run",
  "modality": "running",
  "time": "07:00",
  "duration_minutes": 30,
  "difficulty": "intermediate"
}]

Valid modalities: yoga, strength, cardio, hiit, running, mobility, core, stretch
Generate ${spw} sessions spread across ${days}.
Use the provided time preference and intensity to set difficulty and time fields.`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkoutBuilderDemo() {
  const [mode, setMode]         = useState<Mode>('build');
  const [sessions, setSessions] = useState<WorkoutDay[]>([]);
  const [survey, setSurvey]     = useState<SurveyAnswers>(blankSurvey());
  const [submitted, setSubmitted] = useState(false);

  // ── Builder helpers ──
  const addSession       = () => setSessions(prev => [...prev, blankSession()]);
  const removeSession    = (i: number) => setSessions(prev => prev.filter((_, idx) => idx !== i));
  const duplicateSession = (i: number) =>
    setSessions(prev => [...prev.slice(0, i + 1), { ...prev[i], day: '' }, ...prev.slice(i + 1)]);

  const setField = <K extends keyof WorkoutDay>(i: number, field: K, value: WorkoutDay[K]) =>
    setSessions(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const setModality = (i: number, mod: string) =>
    setSessions(prev => prev.map((s, idx) => {
      if (idx !== i) return s;
      const nameIsDefault = ALL_DEFAULT_NAMES.has(s.name) || s.name === '';
      return {
        ...s,
        modality:       mod,
        name:           nameIsDefault ? (DEFAULT_NAMES[mod] ?? 'Workout') : s.name,
        notes:          '',
        strength_focus: mod === 'strength' ? s.strength_focus : '',
        equipment:      mod === 'strength' ? s.equipment : '',
      };
    }));

  const togglePill = <K extends 'strength_focus' | 'equipment'>(i: number, field: K, val: string) =>
    setField(i, field, sessions[i][field] === val ? '' : val);

  // ── Survey helpers ──
  const toggleSurveyMulti = (field: 'workout_types' | 'best_days', val: string) =>
    setSurvey(prev => {
      const cur = prev[field];
      return { ...prev, [field]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] };
    });

  const setSurveyField = (field: keyof SurveyAnswers, val: string) =>
    setSurvey(prev => ({ ...prev, [field]: val }));

  const surveyValid = survey.workout_types.length > 0 && survey.best_days.length > 0;
  const buildValid  = sessions.length > 0;

  // ── Submitted screen ──
  if (submitted) {
    const isSurvey = mode === 'survey';
    return (
      <div className="wb-page">
        <div className="wb-root">
          <div className="wb-container">
            <div className="wb-success">
              <div className="wb-success-icon">✓</div>
              <h2 className="wb-success-title">{isSurvey ? 'Survey submitted!' : 'Routine built!'}</h2>
              <p className="wb-success-msg">
                {isSurvey
                  ? 'In production, this is sent to GPT-4 which generates a tailored weekly schedule, then creates your full 4-week plan automatically.'
                  : 'In production, this kicks off AI generation of exercises for each session and creates your 4-week plan.'}
              </p>

              {isSurvey && (
                <div className="wb-ai-prompt-box">
                  <p className="wb-ai-prompt-label">AI prompt that would be sent</p>
                  <p className="wb-ai-prompt-text">{buildAiPrompt(survey)}</p>
                </div>
              )}

              {!isSurvey && (
                <div className="wb-ai-prompt-box">
                  <p className="wb-ai-prompt-label">Sessions payload</p>
                  <pre style={{ fontSize: 12, margin: 0, overflowX: 'auto', color: '#555' }}>
                    {JSON.stringify(sessions, null, 2)}
                  </pre>
                </div>
              )}

              <button className="wb-btn-secondary" onClick={() => setSubmitted(false)}>
                Back to editor
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wb-page">
      <div className="wb-root">
        <div className="wb-container">

          <div className="wb-header">
            <h1 className="wb-title">Build Your Routine</h1>
            <p className="wb-subtitle">
              Choose how you'd like to set up your workout plan.
            </p>
            <p className="wb-demo-tag">Demo — no data is saved</p>
          </div>

          {/* Mode switcher */}
          <div className="wb-mode-row">
            <button
              className={`wb-mode-btn${mode === 'build' ? ' wb-mode-btn--active' : ''}`}
              onClick={() => setMode('build')}
            >
              <span className="wb-mode-label">Build it myself</span>
              <span className="wb-mode-desc">Choose your days, times, and workout types</span>
            </button>
            <button
              className={`wb-mode-btn${mode === 'survey' ? ' wb-mode-btn--active' : ''}`}
              onClick={() => setMode('survey')}
            >
              <span className="wb-mode-label">Quick survey</span>
              <span className="wb-mode-desc">Answer a few questions, AI builds your plan</span>
            </button>
          </div>

          {/* ── Build mode ── */}
          {mode === 'build' && (
            <>
              <div className="wb-session-list">
                {sessions.map((session, i) => (
                  <div className="wb-card" key={i}>
                    <div className="wb-card-header">
                      <select
                        className="wb-day-select"
                        value={session.day}
                        onChange={e => setField(i, 'day', e.target.value)}
                      >
                        <option value="">Select day...</option>
                        {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <button className="wb-remove-btn" onClick={() => removeSession(i)} aria-label="Remove">×</button>
                    </div>

                    <div className="wb-card-body">

                      <div className="wb-field">
                        <span className="wb-field-label">Session name</span>
                        <input type="text" className="wb-input" value={session.name}
                          onChange={e => setField(i, 'name', e.target.value)}
                          placeholder="e.g. Morning Run" maxLength={80} />
                      </div>

                      <div className="wb-field">
                        <span className="wb-field-label">Time</span>
                        <select className="wb-select" value={session.time}
                          onChange={e => setField(i, 'time', e.target.value)}>
                          {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>

                      <div className="wb-field">
                        <span className="wb-field-label">Type</span>
                        <div className="wb-pills">
                          {MODALITY_OPTIONS.map(opt => (
                            <button key={opt.value} type="button"
                              className={`wb-pill${session.modality === opt.value ? ' wb-pill--active' : ''}`}
                              onClick={() => setModality(i, opt.value)}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {NOTES_MODALITIES.has(session.modality) && (
                        <div className="wb-field">
                          <span className="wb-field-label">{NOTES_LABEL[session.modality] ?? 'Activity'}</span>
                          <input type="text" className="wb-input" value={session.notes}
                            onChange={e => setField(i, 'notes', e.target.value)}
                            placeholder={NOTES_PLACEHOLDER[session.modality] ?? ''} maxLength={80} />
                        </div>
                      )}

                      {session.modality === 'strength' && (
                        <div className="wb-sub-section">
                          <div className="wb-field">
                            <span className="wb-field-label">Focus area</span>
                            <div className="wb-pills">
                              {STRENGTH_FOCUS.map(opt => (
                                <button key={opt.value} type="button"
                                  className={`wb-pill${session.strength_focus === opt.value ? ' wb-pill--active' : ''}`}
                                  onClick={() => togglePill(i, 'strength_focus', opt.value)}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="wb-field">
                            <span className="wb-field-label">Equipment</span>
                            <div className="wb-pills">
                              {EQUIPMENT.map(opt => (
                                <button key={opt.value} type="button"
                                  className={`wb-pill${session.equipment === opt.value ? ' wb-pill--active' : ''}`}
                                  onClick={() => togglePill(i, 'equipment', opt.value)}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="wb-field-row">
                        <div className="wb-field">
                          <span className="wb-field-label">Duration</span>
                          <select className="wb-select" value={session.duration_minutes}
                            onChange={e => setField(i, 'duration_minutes', Number(e.target.value))}>
                            {DURATION_OPTIONS.map(m => <option key={m} value={m}>{m} min</option>)}
                          </select>
                        </div>
                        <div className="wb-field">
                          <span className="wb-field-label">Level</span>
                          <select className="wb-select" value={session.difficulty}
                            onChange={e => setField(i, 'difficulty', e.target.value as WorkoutDay['difficulty'])}>
                            {DIFFICULTY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="wb-field">
                        <span className="wb-field-label">Anything else?</span>
                        <textarea className="wb-textarea" rows={2} value={session.other_notes}
                          onChange={e => setField(i, 'other_notes', e.target.value)}
                          placeholder="Injuries, equipment limits, goals for this session..."
                          maxLength={300} />
                      </div>

                      <button className="wb-duplicate-btn" onClick={() => duplicateSession(i)}>
                        + Duplicate to another day
                      </button>

                    </div>
                  </div>
                ))}
              </div>

              <button className="wb-add-btn" onClick={addSession}>+ Add Workout Session</button>

              <div className="wb-nav">
                <button className="wb-btn-primary" disabled={!buildValid} onClick={() => setSubmitted(true)}>
                  Build My Routine
                </button>
              </div>
            </>
          )}

          {/* ── Survey mode ── */}
          {mode === 'survey' && (
            <>
              <div className="wb-survey-card">

                <div className="wb-survey-section">
                  <p className="wb-survey-q">What types of workouts do you enjoy?</p>
                  <p className="wb-survey-hint">Pick as many as you like</p>
                  <div className="wb-pills">
                    {SURVEY_WORKOUT_TYPES.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.workout_types.includes(opt.value) ? ' wb-pill--active' : ''}`}
                        onClick={() => toggleSurveyMulti('workout_types', opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-divider" />

                <div className="wb-survey-section">
                  <p className="wb-survey-q">Which days usually work best?</p>
                  <p className="wb-survey-hint">Select all that apply</p>
                  <div className="wb-pills">
                    {SURVEY_DAYS.map(d => (
                      <button key={d} type="button"
                        className={`wb-pill${survey.best_days.includes(d) ? ' wb-pill--active' : ''}`}
                        onClick={() => toggleSurveyMulti('best_days', d)}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-survey-section">
                  <p className="wb-survey-q">What time of day works best?</p>
                  <div className="wb-pills">
                    {SURVEY_TIMES.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.preferred_time === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('preferred_time', survey.preferred_time === opt.value ? '' : opt.value)}>
                        {opt.label} <span style={{ opacity: 0.6, fontSize: 11 }}>· {opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-divider" />

                <div className="wb-survey-section">
                  <p className="wb-survey-q">How many days per week?</p>
                  <div className="wb-pills">
                    {SURVEY_SPW.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.sessions_per_week === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('sessions_per_week', survey.sessions_per_week === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-survey-section">
                  <p className="wb-survey-q">How long should each session be?</p>
                  <div className="wb-pills">
                    {SURVEY_DURATION.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.duration === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('duration', survey.duration === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-survey-section">
                  <p className="wb-survey-q">How intense should workouts be?</p>
                  <div className="wb-pills">
                    {SURVEY_INTENSITY.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.intensity === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('intensity', survey.intensity === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-divider" />

                <div className="wb-survey-section">
                  <p className="wb-survey-q">Anything else we should know?</p>
                  <p className="wb-survey-hint">Injuries, equipment access, specific goals...</p>
                  <textarea className="wb-textarea" rows={3} value={survey.other_notes}
                    onChange={e => setSurveyField('other_notes', e.target.value)}
                    placeholder="e.g. I have a bad knee. I only have dumbbells at home. I'm training for a 5K."
                    maxLength={400} />
                </div>

              </div>

              <div className="wb-nav">
                <button className="wb-btn-primary" disabled={!surveyValid} onClick={() => setSubmitted(true)}>
                  Generate My Plan
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
